import { Element } from "@xmpp/xml"

export const NAVIGATOR_VEHICLE_DETAILS_COMMAND_NODE =
  "get_navigator_vehicle_details"

export type NavigatorVehiclePlaceDetails = {
  placeId: string
  transportId: string
  syncKey?: string
  kind: "pickup" | "delivery" | "other"
  kindLabel: string
  title: string
  subtitle?: string
  countryCode?: string
  scheduledTime?: string
  realTime?: string
  externalStatus?: string
  state?: string
  addressCity?: string
  addressZipCode?: string
  loadingNumber?: string
  packaging?: string
  weight?: number
  weightUnit?: string
  amount?: number
  description?: string
  comments?: string
  contactPerson?: string
  contactPersonNumber?: string
  isOnRoute?: boolean
  estimatedArrivalTime?: string
  estimatedArrivalTimeWithTacho?: string
  timelinessStatus?: string
  delaySeconds?: number
  delayWithTachoSeconds?: number
  sequence?: number
}

export type NavigatorTransportAttachment = {
  id: string
  fileName?: string
  addedAt?: string
  direction?: string
}

export type NavigatorVehicleTransportDetails = {
  transportId: string
  number?: string
  customer?: string
  route?: string
  truck?: string
  status?: string
  startTime?: string
  finishTime?: string
  startRealTime?: string
  finishRealTime?: string
  isCompleted: boolean
  hasOpenStops: boolean
  cargo?: string
  cargoAmount?: string
  weightKg?: number
  freightEurNet?: number
  freightPlnNet?: number
  kmTotal?: number
  kmWithCargo?: number
  kmWithoutCargo?: number
  clientOrderNumber?: string
  shipmentNotes?: string
  orderAttachment?: NavigatorTransportAttachment
  attachments?: NavigatorTransportAttachment[]
  unitAmount?: number
  unitAmountType?: string
  unitPrice?: number
  unitPriceCurrency?: string
  carrierCharges?: number
  carrierChargesCurrency?: string
  toll?: number
  tollCurrency?: string
  description?: string
  requirements?: string
  commentsForCarrier?: string
  commentsFromCarrier?: string
  places: NavigatorVehiclePlaceDetails[]
}

export type NavigatorVehicleDetails = {
  entityJid?: string
  chatJid?: string
  vehicleId?: string
  gpsInfoId?: number
  instanceHost?: string
  vehicle?: {
    id: string
    plateNumber?: string
    make?: string
    model?: string
  }
  generatedAt?: string
  lookbackHours?: number
  error?: string
  transports: NavigatorVehicleTransportDetails[]
}

const COMMAND_NS = "http://jabber.org/protocol/commands"
const JSON_NS = "urn:xmpp:json:0"

const isDetailsPayload = (value: unknown): value is NavigatorVehicleDetails => {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as NavigatorVehicleDetails).transports)
  )
}

export type NavigatorTransportTimelineSlots = {
  previous?: NavigatorVehicleTransportDetails
  current?: NavigatorVehicleTransportDetails
  next?: NavigatorVehicleTransportDetails
}

const transportSortTime = (transport: NavigatorVehicleTransportDetails) =>
  transport.startTime || transport.finishTime || ""

export const isTransportCompleted = (
  transport: NavigatorVehicleTransportDetails,
) => transport.isCompleted

const isTransportStarted = (
  transport: NavigatorVehicleTransportDetails,
  now: Date,
) => {
  if (transport.startRealTime) return true
  if (!transport.startTime) return false
  return new Date(transport.startTime).getTime() <= now.getTime()
}

/** Split transports into previous (last completed), current and next. */
export const classifyTransports = (
  transports: NavigatorVehicleTransportDetails[],
  now: Date = new Date(),
): NavigatorTransportTimelineSlots => {
  const sorted = [...transports].sort((a, b) =>
    transportSortTime(a).localeCompare(transportSortTime(b)),
  )
  const completed = sorted.filter(isTransportCompleted)
  const open = sorted.filter((transport) => !isTransportCompleted(transport))
  const started = open.filter((transport) => isTransportStarted(transport, now))
  // A later transport whose planned start merely passed on the clock must
  // not hijack the current slot while an earlier, delayed transport is
  // still being realized — prefer transports Navigator actually saw start.
  const actuallyStarted = started.filter((transport) => transport.startRealTime)

  const current = actuallyStarted.at(-1) ?? started.at(-1) ?? undefined
  const next = open.find(
    (transport) =>
      transport !== current &&
      !transport.startRealTime &&
      (!current || transportSortTime(transport) >= transportSortTime(current)),
  )
  return {
    previous: completed.at(-1) ?? undefined,
    current,
    next,
  }
}

const NavigatorVehicleDetailsParser = {
  parse(stanza: Element): NavigatorVehicleDetails | undefined {
    const command = stanza.getChild("command", COMMAND_NS)
    const jsonElement = command?.getChild("json", JSON_NS)
    if (!jsonElement) return undefined

    try {
      const parsed = JSON.parse(jsonElement.text())
      return isDetailsPayload(parsed) ? parsed : undefined
    } catch {
      return undefined
    }
  },
}

export default NavigatorVehicleDetailsParser
